import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Link, Typography, useTheme, Drawer } from '@mui/material';
import { GridSortDirection } from '@mui/x-data-grid-pro';
import { MainHeader } from '@eposnow/ui-core';
import { InfoOutlined } from '@mui/icons-material';
import { GetTransactionsDTO, useTransactionsPaged } from '../../api/transactions';
import { SettingsContext } from '../../context/SettingsContext';
import { TransactionFiltersContext } from '../../context/TransactionFiltersContext';
import { addDismissedBanner, defer, getDismissedBanners, hasFeature } from '../../helpers/helpers';
import PageCache, { CacheType } from '../../helpers/pageCache';
import { TransactionSummary } from '../../models/transactions';
import ReportTable from './components/ReportTable';
import { UserContext } from '../../context/UserContext';
import { UIContext } from '../../context/UIContext';
import ReportListMobile from './components/ReportListMobile';
import { ROWS_PER_PAGE_MOBILE } from './transactionsHelpers';
import { isDateWithinRange, MILLIS_PER_DAY } from '../../helpers/dateUtils';
import WarningBanner from '../../components/WarningBanner';
import { PrintContextProvider } from './context/PrintContext';

import { Banner, BANNER_ACTION_INTERESTED, respondToBanner, useActiveBanners, useBannerUserActions } from '../../api/promotionalBanner';
import { DASHBOARD_DEFAULT_PERMISSIONS } from '../../constants';
import { calculateAcces } from '../../components/FeatureGuardContainer';
import PromotionalBanner from '../../components/PromotionalBanner';
import { useDashboardAccess } from '../../api/useDashboardAccess';

const cache = new PageCache<TransactionSummary>();
const cacheAdmin = new PageCache<TransactionSummary>(CacheType.NO_CACHE);
const SHOW_DATA_DELAY_NOTICE_TRANSACTIONS = 'alerts.dataDelayTransactions';
const BANNER_HEIGHT = 214;

const Transactions = (props: { cacheType?: CacheType; adminView?: boolean }) => {
  // eslint-disable-next-line react/destructuring-assignment
  const navigate = useNavigate();
  const theme = useTheme();
  const { isMobile, isTablet } = useContext(UIContext);
  const { filters, isLoading: isLoadingFiltersContext, isError: isErrorFiltersContext, refetch: refetchFiltersContext } = useContext(TransactionFiltersContext);
  const { settings } = useContext(SettingsContext);
  const { locale, labsFeatures } = useContext(UserContext);
  const hasFeatureCCSurcharge = hasFeature(labsFeatures, 'CCSurcharge');
  const { view } = settings;
  const { cacheType, adminView } = props;
  const [descriptionHeader, setDescriptionHeader] = useState(false)
  const [bannerOpen, setBannerOpen] = useState(true);
  const { refetchAppsMenu } = useContext(UserContext);
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [submitErrors, setSubmitErrors] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = useState<Record<string, boolean>>(
    () =>
        Object.fromEntries(
            (getDismissedBanners() ?? []).map(id => [id, true])
        )
  );

  const { data: banners = [], isLoading } = useActiveBanners('transactions');

  useEffect(() => {
    if (cacheType) {
      cache.setCacheType(cacheType);
      cacheAdmin.setCacheType(cacheType);
    }
  }, [cacheType]);

  useEffect(() => {
    const showDataBanner = +(localStorage.getItem(SHOW_DATA_DELAY_NOTICE_TRANSACTIONS) || '');
    setBannerOpen(showDataBanner <= Date.now());
  }, []);

  const { t } = useTranslation();
  const defaultRowLimit = (isMobile || isTablet) ? ROWS_PER_PAGE_MOBILE : parseInt(settings.rowsPerPage, 10);
  const location = useLocation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultRowLimit);
  const [order, setOrder] = useState('asc' as GridSortDirection);
  const [orderBy, setOrderBy] = useState('date');
  // so basically when we change the view a new fetch to the API must be done.
  // if the "current" view differs from the view we last fetched, that means that we just done the switch
  // and we are rerendering, but we are still awaiting to fetch the data as it is a deferred call
  // we should treat this as a loading status.
  const [lastFetchedDataView, setLastFetchedDataView] = useState(view);
  const [totalDataFetched, setTotalDataFetched] = useState<TransactionSummary[]>([]);

  const {
    hasDashboardAccess,
    isLoading: isLoadingDashboardAccess,
} = useDashboardAccess();


  const linkNavigation = useCallback(
    (path: string | undefined) => {
      if (path) {
        navigate(path);
      }
    },
    [navigate]
  );

  const {
    data: apiData,
    refetch: fetchTransactions,
    isFetching,
    isError,
  } = useTransactionsPaged<TransactionSummary>(
    {
      startDate: filters.periodFrom,
      endDate: filters.periodTo,
      location: filters.location ? filters.location.id : undefined,
      device: filters.device ? filters.device.id : undefined,
      locationArea: filters.locationArea ? filters.locationArea.id : undefined,
      staff: filters.staff ? filters.staff.id : undefined,
      paymentMethod: filters.paymentMethod ? filters.paymentMethod.id : undefined,
      search: search || undefined,
      page,
      limit,
      sortBy: orderBy,
      sortOrder: order,
    } as GetTransactionsDTO,
    cache,
    'transactions',
    {
      enabled: !isLoadingFiltersContext && !adminView,
    },
    false
  );

  const {
    data: apiDataAdmin,
    refetch: fetchTransactionsAdmin,
    isFetching: isFetchingAdmin,
    isError: isErrorAdmin,
  } = useTransactionsPaged<TransactionSummary>(
    {
      startDate: filters.periodFrom,
      endDate: filters.periodTo,
      search: search || undefined,
      page,
      limit,
      location: filters.location ? filters.location.id : undefined,
      device: filters.device ? filters.device.id : undefined,
      sortBy: orderBy,
      sortOrder: order,
      companyGuid: filters.companyGuid,
      hasActiveFlag: filters.hasActiveFlag,
      activeFlagCategory: filters.activeFlagCategory,
    } as GetTransactionsDTO,
    cacheAdmin,
    'transactionsAdmin',
    {
      enabled: !isLoadingFiltersContext && !!adminView,
    },
    true
  );

  useEffect(() => {
    if (apiData?.data && !adminView) {
      setTotalDataFetched((prev) => [...prev, ...apiData.data]);
      setLastFetchedDataView(view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData, adminView, view]);

  useEffect(() => {
    if (apiDataAdmin?.data && adminView) {
      setTotalDataFetched((prev) => [...prev, ...apiDataAdmin.data]);
      setLastFetchedDataView(view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiDataAdmin, adminView, view]);

  const refetch = useCallback(async () => {
    if (isLoadingFiltersContext) return; // avoid refetching while filters are loading
    refetchFiltersContext();
    if (!adminView) {
      await fetchTransactions();
    }
    if (adminView) {
      await fetchTransactionsAdmin();
    }
  }, [refetchFiltersContext, adminView, fetchTransactions, fetchTransactionsAdmin]);

  // support is only 'es' or 'en_US' currently and 'en' forwards on
  let supportLink = `https://support.eposnow.com/s/article/Transaction-Report?language=${locale.substring(0, 2)}`;
  if (locale.substring(0, 2) === 'es') {
    supportLink = 'https://support.eposnow.com/s/article/Informe-Transacciones?language=es'
  }

  // Force reset the page and fetch data on filters or admin view change
  useEffect(
    () =>
      defer(() => {
        if (isErrorFiltersContext) return; // prevent infinite loop on error
        setTotalDataFetched([]);
        setPage(0);
        refetch().then();
      }),
    [isErrorFiltersContext, filters, search, refetch, location]
  )

  function addSurchargeToTax(transaction: TransactionSummary): TransactionSummary {
    return { ...transaction, tax: transaction.tax + transaction.surcharge.totalTax };
  }

  const processedData = useMemo(() => {
    const rawData = apiData?.data ?? [];

    return hasFeatureCCSurcharge
      ? rawData.map(addSurchargeToTax)
      : rawData;
  }, [apiData, hasFeatureCCSurcharge]);

  const processedAdminData = useMemo(() => {
    const rawData = apiDataAdmin?.data ?? [];

    return hasFeatureCCSurcharge
      ? rawData.map(addSurchargeToTax)
      : rawData;
  }, [apiDataAdmin, hasFeatureCCSurcharge]);

  const { bannerUserActions, isLoading: isLoadingBanner, isError: isErrorBanner, refetch: refetchBannerUserActions } = useBannerUserActions();

  const shouldShowBanner = (banner: Banner) => {
    if (hasDashboardAccess || isLoadingDashboardAccess) {
        return false;
    }

    const hasInteracted =
        dismissed[banner.id] ||
        bannerUserActions?.some(
            action =>
                action.bannerId === banner.id &&
                action.action === BANNER_ACTION_INTERESTED
        );

    return (
        !hasInteracted &&
        isDateWithinRange(
            new Date(),
            banner.startDate,
            banner.endDate
        )
    );
};

  const handleInterested = async (banner: Banner) => {
    setSubmitting(prev => ({
        ...prev,
        [banner.id]: true,
    }));

    setSubmitErrors(prev => ({
        ...prev,
        [banner.id]: false,
    }));

    try {
        await respondToBanner(
            banner.id,
            BANNER_ACTION_INTERESTED,
            maybeAccessToken
        );

        await queryClient.invalidateQueries({
            queryKey: ['bannerUserActions'],
        });

        navigate('/dashboards');
    } catch {
        setSubmitErrors(prev => ({
            ...prev,
            [banner.id]: true,
        }));
    } finally {
        setSubmitting(prev => ({
            ...prev,
            [banner.id]: false,
        }));
    }
};

  const handleDismiss = (banner: Banner) => {
    addDismissedBanner(banner.id);

    setDismissed(prev => ({
      ...prev,
      [banner.id]: true,
    }));
  };

const shouldShowMaintenanceBanner = (banner: Banner): boolean =>
  !dismissed[banner.id] &&
  isDateWithinRange(new Date(), banner.startDate, banner.endDate);

const shouldShowPromotionBanner = (banner: Banner): boolean => {
  if (hasDashboardAccess) {
    return false;
  }

  const hasInteracted =
    dismissed[banner.id] ||
    (bannerUserActions?.some(
      action =>
        action.bannerId === banner.id &&
        action.action === BANNER_ACTION_INTERESTED
    ) ?? false);

  return !hasInteracted &&
    isDateWithinRange(new Date(), banner.startDate, banner.endDate);
};

  

  const promotionBanners = useMemo(
  () =>
    banners.filter(
      (banner) =>
        banner.type === 'promotion' &&
        shouldShowPromotionBanner(banner)
    ),
  [banners, bannerUserActions, dismissed, hasDashboardAccess]
);

const maintenanceBanners = useMemo(
  () =>
    banners.filter(
      (banner) =>
        banner.type === 'maintenance' &&
        shouldShowMaintenanceBanner(banner)
    ),
  [banners, dismissed]
);

const visibleBannersHeigth = useMemo(
  () =>
    banners.filter((banner) =>
      banner.type === 'promotion'
        ? shouldShowPromotionBanner(banner)
        : shouldShowMaintenanceBanner(banner)
    ).length * BANNER_HEIGHT,
  [banners, bannerUserActions, dismissed, hasDashboardAccess]
);


  return (
    <>
      <>
  {promotionBanners.map((banner) => (
            <PromotionalBanner
              key={banner.id}
              banner={banner}
              onInterested={() => handleInterested(banner)}
              onDismiss={() => handleDismiss(banner)}
              isLoading={isLoadingBanner}
              isSubmitting={!!submitting[banner.id]}
              submitError={!!submitErrors[banner.id]}
              isError={isErrorBanner}
            />
          )
        )}
  {maintenanceBanners.map((banner) => (
        <WarningBanner
          key={banner.id}
          banner={banner}
          onDismiss={() => handleDismiss(banner)}
            />
          )
        )}


      </>
      {!isMobile && !isTablet &&
        <Box
          sx={{
            marginLeft: 0,
            marginBottom: { xs: 1, sm: 2 },
            paddingX: { xs: 2, sm: 0 },
          }}
        >
          <MainHeader
            title={adminView ? t('nav.transactionsAdmin') : t('screens.transactions.title')}
            nav={[
              {
                text: t('nav.reporting'),
                link: '/',
                icon: <ShowChartOutlinedIcon sx={{ mr: 1 }} fontSize="inherit" />,
                dataQaId: 'headerNavTransactionReports',
              },
              {
                text: adminView ? t('nav.transactionsAdmin') : t('nav.transactions'),
                link: '/transactions',
                icon: adminView ? (
                  <AdminPanelSettingsOutlinedIcon sx={{ mr: 1 }} fontSize="inherit" />
                ) : (
                  <SummarizeOutlinedIcon sx={{ mr: 1 }} fontSize="inherit" />
                ),
                dataQaId: 'headerNavCompletedTransactionReports'
              },
            ]}
            theme={theme}
            linkNavigationFunction={linkNavigation}
          >
            <Typography variant="body1">
              {t('screens.transactions.description')}
              <Link
                href={supportLink}
                sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
              >
                {t('screens.transactions.learnMore')}
              </Link>
            </Typography>
            <Typography variant="body1">
              {t('screens.transactions.classicLinkPreText')}
              <Link
                href={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/CompleteTransactions.aspx`}
                sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
              >
                {t('screens.transactions.classicLinkText')}
              </Link>
            </Typography>
          </MainHeader>
        </Box>
      }
      {(isMobile || isTablet) &&
        <Box
          sx={{
            marginLeft: 0,
            marginBottom: { xs: 1, sm: 2 },
            paddingX: { xs: 2, sm: 0 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography
              variant='h2'
            >
              {adminView ? t('nav.transactionsAdmin') : t('screens.transactions.title')}
            </Typography>
            <InfoOutlined
              onClick={() => setDescriptionHeader(!descriptionHeader)}
              sx={{
                marginLeft: '4px',
              }}
            />
          </Box>
          <Drawer
            anchor="bottom"
            open={descriptionHeader}
            onClose={() => setDescriptionHeader(false)}
            sx={{
              '& .MuiDrawer-paper': {
                padding: '24px',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                gap: '24px',
              },
            }}
          >
            <Typography variant="body1">
              {t('screens.transactions.description')}
              <Link
                href={supportLink}
                sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
              >
                {t('screens.transactions.learnMore')}
              </Link>
            </Typography>
            <Typography variant="body1">
              {t('screens.transactions.classicLinkPreText')}
              <Link
                href={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/CompleteTransactions.aspx`}
                sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
              >
                {t('screens.transactions.classicLinkText')}
              </Link>
            </Typography>
          </Drawer>
        </Box>
      }
      <PrintContextProvider>
        {!adminView && !(isMobile || isTablet) && (
          <ReportTable
            key="userTable"
            rows={processedData}
            refetch={refetch}
            refresh={() => {
              cache.clear();
              refetch().then();
            }}
            isError={isErrorFiltersContext || isError}
            search={search}
            setSearch={setSearch}
            page={page}
            setPage={setPage}
            rowsPerPage={limit}
            setRowsPerPage={setLimit}
            isLoading={isLoadingFiltersContext || isFetching}
            order={order as GridSortDirection}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            count={apiData ? apiData.pagination.totalRecords : 0}
            extraFiltersHeigth={visibleBannersHeigth}
          />)
        }
        {adminView && !(isMobile || isTablet) && (
          <ReportTable
            key="adminTable"
            rows={processedAdminData}
            refetch={refetch}
            refresh={() => {
              cache.clear();
              refetch().then();
            }}
            isError={isErrorFiltersContext || isErrorAdmin}
            search={search}
            setSearch={setSearch}
            page={page}
            setPage={setPage}
            rowsPerPage={limit}
            setRowsPerPage={setLimit}
            isLoading={isLoadingFiltersContext || isFetchingAdmin || view !== lastFetchedDataView}
            order={order as GridSortDirection}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            count={apiDataAdmin ? apiDataAdmin.pagination.totalRecords : 0}
            adminView
            extraFiltersHeigth={visibleBannersHeigth}
          />)
        }
      </PrintContextProvider>
      {(isMobile || isTablet) && (
        <ReportListMobile
          key="mobileList"
          refetch={refetch}
          refresh={() => {
            cache.clear();
            refetch().then();
          }}
          page={page}
          setPage={setPage}
          setRowsPerPage={setLimit}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          adminView={adminView}
          setOrderBy={setOrderBy}
          count={apiData ? apiData.pagination.totalRecords : 0}
          isLoading={isLoadingFiltersContext || isFetching || view !== lastFetchedDataView}
          isError={isErrorFiltersContext || isError}
          totalRows={totalDataFetched}
          emptyTotalRows={() => setTotalDataFetched([])}
          search={search}
          setSearch={setSearch}
        />
      )}
    </>
  );
};

export default Transactions;
